/**
 * Raw Telemetry Processing Stage
 *
 * Processes raw sensor readings into processed telemetry data
 */

import {
  PipelineStage,
  PipelineContext,
  StageResult,
} from '../ProcessingPipeline'
import { processRawTelemetry } from '../../rawTelemetryProcessor'

export class RawTelemetryProcessingStage implements PipelineStage {
  name = 'Raw Telemetry Processing'

  async execute(context: PipelineContext): Promise<StageResult<void>> {
    const warnings: string[] = []

    try {
      const { rawTelemetry, options, dto } = context

      if (!rawTelemetry) {
        return {
          success: false,
          error: 'No raw telemetry data available',
        }
      }

      // Determine session start time (convert from string if needed, handle null metadata)
      const dtoStartTime = dto.metadata?.sessionStartTime
        ? dto.metadata.sessionStartTime instanceof Date
          ? dto.metadata.sessionStartTime
          : new Date(dto.metadata.sessionStartTime)
        : undefined

      const sessionStartTime =
        dtoStartTime ??
        rawTelemetry.recordingStartTime ??
        rawTelemetry.gps[0]?.timestamp ??
        new Date()

      // Process raw telemetry with options from pipeline
      const processed = processRawTelemetry(rawTelemetry, sessionStartTime, {
        calculateLeanAngle: options.calculateLeanAngle,
        calculateAcceleration: options.calculateAcceleration,
        calculateGForce: options.calculateGForce,
        smoothingWindow: options.enableSmoothing
          ? options.smoothingWindow
          : undefined,
      })

      context.processedTelemetry = processed

      // Check data quality
      if (processed.gpsPoints.length === 0) {
        return {
          success: false,
          error: 'No GPS points were processed',
        }
      }

      if (options.calculateLeanAngle && !processed.leanAngle) {
        warnings.push('Lean angle calculation was requested but not generated')
      }

      if (options.calculateAcceleration && !processed.acceleration) {
        warnings.push(
          'Acceleration calculation was requested but not generated'
        )
      }

      if (options.calculateGForce && !processed.gForce) {
        warnings.push('G-force calculation was requested but not generated')
      }

      return {
        success: true,
        warnings: warnings.length > 0 ? warnings : undefined,
      }
    } catch (error) {
      return {
        success: false,
        error: `Raw telemetry processing failed: ${error instanceof Error ? error.message : String(error)}`,
      }
    }
  }
}
